import { useThemeStore, type PageStyle } from '../stores/theme'

/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 *
 * Composable for accessing and changing themes
 * This can be used in any component, including views, to control the app theme
 */
export default function useTheme() {
  const themeStore = useThemeStore()
  
  /**
   * Set the current theme (only applies theme classes like theme-xw-teal)
   * @param themeName - Theme name without the 'theme-' prefix (e.g., 'xw-white')
   */
  function setTheme(themeName: string): void {
    themeStore.setAppTheme(themeName)
  }
  
  /**
   * Convert Strapi theme enum format to app theme format
   * @param strapiTheme - Theme from Strapi enum (e.g., 'xw_white')
   * @returns App theme format (e.g., 'xw-white')
   */
  function convertStrapiTheme(strapiTheme?: string): string | null {
    if (!strapiTheme) return null
    // Convert underscores to hyphens for app theme format
    return strapiTheme.replace(/_/g, '-')
  }
  
  /**
   * Set theme from Strapi pageStyle data (theme + pageTopSpacer)
   * @param pageStyle - PageStyle object from Strapi
   * @param fallbackTheme - Fallback theme if none specified (default: 'xw-white')
   */
  function setPageStyleFromStrapi(pageStyle: PageStyle | null | undefined, fallbackTheme = 'xw-white'): void {
    if (pageStyle?.theme) {
      const appTheme = convertStrapiTheme(pageStyle.theme)
      if (appTheme) {
        setTheme(appTheme)
      } else {
        setTheme(fallbackTheme)
      }
    } else {
      setTheme(fallbackTheme)
    }
    
    // Set pageTopSpacer from backend data
    themeStore.setPageTopSpacer(pageStyle?.pageTopSpacer)
  }
  
  /**
   * Set theme for panel pages (when panels open)
   * @param pageStyle - PageStyle object from panel page data
   * @param fallbackTheme - Fallback theme if none specified
   */
  function setPanelTheme(pageStyle: PageStyle | null | undefined, fallbackTheme = 'xw-white'): void {
    // Store the current main theme and enter panel mode
    themeStore.enterPanelMode()
    
    if (pageStyle?.theme) {
      const appTheme = convertStrapiTheme(pageStyle.theme)
      if (appTheme) {
        setTheme(appTheme)
      } else {
        setTheme(fallbackTheme)
      }
    } else {
      setTheme(fallbackTheme)
    }
  }
  
  /**
   * Restore the previous theme (when panels close)
   */
  function restorePreviousTheme(): void {
    themeStore.restorePreviousTheme()
  }
  
  /**
   * Set theme for main pages (only works when not in panel mode)
   * @param themeName - Theme name without the 'theme-' prefix
   */
  function setMainPageTheme(themeName: string): void {
    themeStore.setMainPageTheme(themeName)
  }
  
  /**
   * Extract and set theme from Strapi page data (backward compatibility)
   * @param strapiData - Strapi content data with pageStyle.theme
   * @param fallbackTheme - Fallback theme if none specified (default: 'xw-white')
   */
  function setThemeFromStrapi(strapiData: any, fallbackTheme = 'xw-white'): void {
    // Check if pageStyle and theme exist in the data
    const pageStyle = strapiData?.pageStyle || strapiData?.attributes?.pageStyle
    setPageStyleFromStrapi(pageStyle, fallbackTheme)
  }
  
  /**
   * Get theme from Strapi data without setting it
   * @param strapiData - Strapi content data with pageStyle.theme
   * @param fallbackTheme - Fallback theme if none specified
   * @returns Theme name
   */
  function getThemeFromStrapi(strapiData: any, fallbackTheme = 'xw-white'): string {
    const pageStyle = strapiData?.pageStyle || strapiData?.attributes?.pageStyle
    const strapiTheme = pageStyle?.theme
    
    if (strapiTheme) {
      const converted = convertStrapiTheme(strapiTheme)
      return converted || fallbackTheme
    }
    
    return fallbackTheme
  }
  
  return {
    currentTheme: themeStore.currentTheme,
    setTheme,
    setMainPageTheme,
    setPageStyleFromStrapi,
    setPanelTheme,
    restorePreviousTheme,
    setThemeFromStrapi,
    getThemeFromStrapi,
    setPageTopSpacer: themeStore.setPageTopSpacer
  }
} 